import React from 'react';
import { Link } from 'react-router-dom';
import I3Modal from './I3Modal';
import { useI3Modal, I3ModalId } from '../context/I3ModalContext';
import { BOOKING_FLOW_ENABLED, BOOK_SESSION_PATH, CONTACT_PATH } from '../bookingConfig';
import { SITE_PRACTITIONER_CREDENTIALS } from '../sitePlaceholders';

const SERVICES_BASE = '/services';

function schedulingCtaLabel(): string {
  return BOOKING_FLOW_ENABLED ? 'Services & scheduling' : 'Contact us';
}

function ModalSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="i3-modal-section" aria-label={title}>
      <h3 className="i3-modal-section-title">{title}</h3>
      {children}
    </section>
  );
}

const modalContents: Record<Exclude<I3ModalId, null>, { title: string; body: React.ReactNode }> = {
  marriage: {
    title: 'Marriage & couples therapy',
    body: (
      <>
        <p className="i3-modal-lead">
          I work with couples who feel distant, stuck in conflict, or unsure how to rebuild trust. Sessions are a
          place to slow down, say the hard things safely, and learn new patterns you can actually use during the
          week—not just in my office.
        </p>
        <ModalSection title="What we address">
          <ul className="i3-modal-list">
            <li>Communication breakdowns and recurring arguments</li>
            <li>Emotional or physical disconnection; intimacy concerns</li>
            <li>Repair after hurt, stress, or major life changes</li>
            <li>Preparing for marriage or recommitting after a hard season</li>
          </ul>
        </ModalSection>
        <ModalSection title="You can expect">
          <ul className="i3-modal-list">
            <li>Structured conversations that reduce blame and increase understanding</li>
            <li>Tools for repair, boundaries, and healthy conflict</li>
            <li>Space for your faith and values when that matters to you</li>
          </ul>
        </ModalSection>
        <div className="i3-modal-actions">
          <Link to={BOOK_SESSION_PATH} className="i3-cta i3-cta-primary">
            {schedulingCtaLabel()}
          </Link>
          <Link to={CONTACT_PATH} className="i3-cta i3-cta-secondary">
            Ask a question
          </Link>
        </div>
      </>
    ),
  },
  leadership: {
    title: 'Individual therapy & mental health',
    body: (
      <>
        <p className="i3-modal-lead">
          One-on-one therapy for adults who need support with anxiety, depression, stress, grief, burnout, identity
          questions, or big transitions. You don&apos;t have to carry it alone—and you don&apos;t need a crisis to
          deserve good care.
        </p>
        <ModalSection title="Common focuses">
          <ul className="i3-modal-list">
            <li>Anxiety, worry, panic, or feeling on edge</li>
            <li>Low mood, fatigue, or loss of motivation</li>
            <li>Grief and loss; life changes (job, move, empty nest)</li>
            <li>Stress that shows up in your body, sleep, or relationships</li>
          </ul>
        </ModalSection>
        <ModalSection title="How I approach it">
          <ul className="i3-modal-list">
            <li>Warm, collaborative sessions at a pace that fits you</li>
            <li>Practical skills plus deeper work when you&apos;re ready</li>
            <li>Coordination with couple or family work when that would help</li>
          </ul>
        </ModalSection>
        <div className="i3-modal-actions">
          <Link to={BOOK_SESSION_PATH} className="i3-cta i3-cta-primary">
            {schedulingCtaLabel()}
          </Link>
          <Link to={CONTACT_PATH} className="i3-cta i3-cta-secondary">
            Ask a question
          </Link>
        </div>
      </>
    ),
  },
  financial: {
    title: 'Family counseling',
    body: (
      <>
        <p className="i3-modal-lead">
          Family therapy helps when tension at home won&apos;t resolve with one more conversation in the kitchen.
          I support parents, co-parents, and blended families who want clearer roles, calmer conflict, and kids who
          feel more secure.
        </p>
        <ModalSection title="What we work on">
          <ul className="i3-modal-list">
            <li>Parenting alignment, discipline, and follow-through</li>
            <li>Blended-family dynamics and loyalty binds</li>
            <li>Teen or young-adult conflict; communication with caregivers</li>
            <li>Family stress after separation, relocation, or trauma</li>
          </ul>
        </ModalSection>
        <ModalSection title="You can expect">
          <ul className="i3-modal-list">
            <li>Everyone gets a voice—age-appropriate and respectful</li>
            <li>Skills you can rehearse between sessions</li>
            <li>Focus on connection and repair, not &quot;winning&quot; the argument</li>
          </ul>
        </ModalSection>
        <div className="i3-modal-actions">
          <Link to={BOOK_SESSION_PATH} className="i3-cta i3-cta-primary">
            {schedulingCtaLabel()}
          </Link>
          <Link to={CONTACT_PATH} className="i3-cta i3-cta-secondary">
            Ask a question
          </Link>
        </div>
      </>
    ),
  },
  speaking: {
    title: 'Workshops & speaking',
    body: (
      <>
        <p className="i3-modal-lead">
          I speak and facilitate for churches, retreats, and community groups on marriage, family, and emotional
          health—always grounded in clinical wisdom and respectful of your audience&apos;s context.
        </p>
        <ModalSection title="Formats">
          <ul className="i3-modal-list">
            <li>Premarital workshops and marriage enrichment sessions</li>
            <li>Breakouts on communication, stress, and healthy families</li>
            <li>Panels and moderated conversations for leaders</li>
          </ul>
        </ModalSection>
        <ModalSection title="Next step">
          <ul className="i3-modal-list">
            <li>Share your date, location, and audience—I&apos;ll follow up with availability</li>
            <li>See details on Services or reach out through Contact</li>
          </ul>
        </ModalSection>
        <div className="i3-modal-actions">
          <Link to={CONTACT_PATH} className="i3-cta i3-cta-primary">
            Contact me
          </Link>
          <Link to={SERVICES_BASE} className="i3-cta i3-cta-secondary">
            View services
          </Link>
        </div>
      </>
    ),
  },
  founders: {
    title: 'About Lisa',
    body: (
      <>
        <p className="i3-modal-lead">
          {SITE_PRACTITIONER_CREDENTIALS}. My practice centers on therapy that is honest, compassionate, and
          practical—whether you&apos;re here for your marriage, your own mental health, or your family system.
        </p>
        <ModalSection title="Why work with me">
          <ul className="i3-modal-list">
            <li>Experience with couples, individuals, and family sessions</li>
            <li>Integration of evidence-based care with your values</li>
            <li>A steady presence when life feels loud or uncertain</li>
          </ul>
        </ModalSection>
        <div className="i3-modal-actions">
          <Link to="/about" className="i3-cta i3-cta-primary">
            Full about page
          </Link>
          <Link to={CONTACT_PATH} className="i3-cta i3-cta-secondary">
            Get in touch
          </Link>
        </div>
      </>
    ),
  },
};

const I3ModalContent: React.FC = () => {
  const { modalId } = useI3Modal();

  if (!modalId) return null;

  const { title, body } = modalContents[modalId];

  return (
    <I3Modal id={modalId} title={title}>
      {body}
    </I3Modal>
  );
};

export default I3ModalContent;
